// components/SensorRow.js - Fixed to never display arrays as objects
// components/SensorRow.js - Fixed to never display arrays as objects
import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import TimelineIcon from '@mui/icons-material/Timeline';
import BarChartIcon from '@mui/icons-material/BarChart';

// Helper function to format sensor values for display
function formatSensorValue(value) {
  if (value === null || value === undefined) {
    return 'N/A';
  }

  // If it's an array, don't display it (BME688 should use BME688Sensor component instead)
  if (Array.isArray(value)) {
    return `[${value.length} items]`;
  }

  // If it's an object, convert to JSON string for display
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch (e) {
      return '[Complex Object]';
    }
  }

  // If it's a number, format it nicely
  if (typeof value === 'number') {
    // Round to 2 decimal places if it's a float
    return Number.isInteger(value) ? value.toString() : value.toFixed(2);
  }

  // For strings and other types, just convert to string
  return String(value);
}

// Helper function to render sensor data fields
function renderSensorFields(sensorData) {
  if (!sensorData || typeof sensorData !== 'object') {
    return <Typography variant="body2" color="text.secondary">No data available</Typography>;
  }

  // Filter out array values (they shouldn't be displayed in regular sensors)
  const entries = Object.entries(sensorData).filter(([key, value]) => !Array.isArray(value));

  if (entries.length === 0) {
    return <Typography variant="body2" color="text.secondary">No data available</Typography>;
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
      {entries.map(([key, value]) => (
        <Box key={key} sx={{ minWidth: '150px' }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>
            {key}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '0.95rem', wordBreak: 'break-word' }}>
            {formatSensorValue(value)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

export default function SensorRow({ sensorName, sensorData, onTimeSeriesClick, onCompositeGraphClick }) {
  const handleTimeSeriesClick = () => {
    if (onTimeSeriesClick) {
      onTimeSeriesClick(sensorName, sensorData);
    }
  };

  const handleCompositeGraphClick = () => {
    if (onCompositeGraphClick) {
      onCompositeGraphClick(sensorName, sensorData);
    }
  };

  return (
    <Paper
      elevation={1}
      sx={{
        p: 3,
        mb: 2,
        borderRadius: 2,
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: 3,
          transform: 'translateY(-2px)'
        }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontSize: '1.2rem' }}>
            {sensorName}
          </Typography>

          {renderSensorFields(sensorData)}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, ml: 2 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<TimelineIcon />}
            onClick={handleTimeSeriesClick}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Time Series
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<BarChartIcon />}
            onClick={handleCompositeGraphClick}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Composite Graph
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}